import { useHeatmap } from '../../hooks/useHeatmap';
import { formatPercent } from '../../utils/formatters';

interface ScrollMapProps {
  url: string;
  device: string;
  dateRange: string;
}

export default function ScrollMap({ url, device, dateRange }: ScrollMapProps) {
  const { data, isLoading } = useHeatmap({ url, type: 'scroll', dateRange, device });

  if (isLoading) {
    return (
      <div className="card flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  const scrollBands = data?.distribution || [];

  return (
    <div className="card">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Scroll Depth Map</h3>
        <p className="text-xs text-gray-500">
          Percentage of visitors reaching each scroll depth
        </p>
      </div>

      {scrollBands.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-300 text-sm text-gray-500">
          No scroll-depth events match these filters.
        </div>
      ) : <div className="relative overflow-hidden rounded-lg border border-gray-200">
        <div className="h-[600px] w-full bg-white">
          {scrollBands.map((band, i) => {
            const height = i < scrollBands.length - 1
              ? `${scrollBands[i + 1].depth - band.depth}%`
              : `${100 - band.depth}%`;

            const opacity = band.percentage / 100;
            const hue = band.percentage > 66 ? 120 : band.percentage > 33 ? 60 : 0;

            return (
              <div
                key={band.depth}
                className="relative w-full border-t border-dashed border-gray-200"
                style={{
                  height,
                  backgroundColor: `hsla(${hue}, 80%, 50%, ${opacity * 0.3})`,
                }}
              >
                <div className="absolute left-4 top-2 flex items-center gap-2">
                  <span className="rounded bg-white/90 px-2 py-0.5 text-xs font-medium text-gray-700 shadow-sm">
                    {band.depth}% scroll depth
                  </span>
                  <span className="rounded bg-white/90 px-2 py-0.5 text-xs font-semibold shadow-sm"
                    style={{ color: `hsl(${hue}, 80%, 40%)` }}
                  >
                    {formatPercent(band.percentage)} of visitors
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>}
    </div>
  );
}
